// Date and time display lives in @/lib/date-format (formatDate, formatDateRange, formatAgo).

export function fmtDays(n: number): string {
  return `${n}d`;
}

export function fmtDelta(n: number): string {
  if (n === 0) return "±0d";
  return n > 0 ? `+${n}d` : `${n}d`;
}

export function fmtPercent(n: number): string {
  return `${Math.round(n)}%`;
}

export function fmtSpi(spi: number | null): string {
  return spi === null ? "—" : spi.toFixed(2);
}

/** 탭 뱃지. 100건 이상은 99+. */
export function formatCountBadge(n: number): string {
  return n > 99 ? "99+" : String(n);
}

/** Korean Won currency formatting with optional privacy masking */
export function fmtCurrency(amount: number | undefined, masked = false): string {
  if (masked) return "₩ •••••••";
  if (amount === undefined || isNaN(amount)) return "—";
  return new Intl.NumberFormat("ko-KR", { style: "currency", currency: "KRW", maximumFractionDigits: 0 }).format(amount);
}

/** Formats Man-Month number with clean decimals */
export function fmtMM(mm: number | undefined): string {
  if (mm === undefined || isNaN(mm)) return "—";
  return `${Number(mm.toFixed(2))} M/M`;
}

/** IME 조합 중 저장되면 "ㅓ"처럼 자모만 남는다. */
export function sanitizeBaselineName(name: string | undefined): string {
  const trimmed = name?.trim() ?? "";
  if (!trimmed || /^[\u1100-\u11FF\u3131-\u318E]+$/.test(trimmed)) return "";
  return trimmed;
}

/** 기준선 표시명. 비어 있거나 미완성 한글이면 "기준선". */
export function displayBaselineName(name: string | undefined): string {
  return sanitizeBaselineName(name) || "기준선";
}

/** 한 플랜에 두는 기준선 수. 승인된 계획(착수·변경) 규모다. */
export const BASELINE_MAX_COUNT = 10;
/** 메뉴와 목록에서 한 줄로 읽는 이름. */
export const BASELINE_NAME_MAX = 40;
/** 관리 목록 둘째 줄에 두는 메모. */
export const BASELINE_NOTE_MAX = 80;

export const BASELINE_LIMIT_ERROR = `기준선은 ${BASELINE_MAX_COUNT}개까지 저장할 수 있습니다.`;

/** 글자 수. 조합 문자 하나를 한 글자로 센다. */
export function limitText(value: string, max: number): string {
  const chars = [...value];
  return chars.length <= max ? value : chars.slice(0, max).join("");
}

/** 빈 이름·미완성 한글은 fallback. 길면 앞에서 자른다. */
export function clampBaselineName(name: string | undefined, fallback: string): string {
  const clean = sanitizeBaselineName(name);
  const base = clean || sanitizeBaselineName(fallback) || "기준선";
  return sanitizeBaselineName(limitText(base, BASELINE_NAME_MAX)) || "기준선";
}

/** 공백만 있으면 메모 없음. */
export function clampBaselineNote(note: string | undefined): string | undefined {
  const trimmed = note?.trim() ?? "";
  if (!trimmed) return undefined;
  return limitText(trimmed, BASELINE_NOTE_MAX);
}

/** 상한이면 null. 저장에 쓸 이름과 메모. */
export function prepareBaselineSave(
  count: number,
  name: string | undefined,
  note: string | undefined,
): { name: string; note?: string } | null {
  if (count >= BASELINE_MAX_COUNT) return null;
  const nextName = clampBaselineName(name, `기준선 ${count + 1}`);
  const nextNote = clampBaselineNote(note);
  return nextNote ? { name: nextName, note: nextNote } : { name: nextName };
}

/** 이름과 메모가 그대로면 null. */
export function prepareBaselineRename(
  currentName: string,
  currentNote: string | undefined,
  name: string | undefined,
  note: string | undefined,
): { name: string; note?: string } | null {
  const nextName = clampBaselineName(name, currentName);
  const nextNote = clampBaselineNote(note);
  if (nextName === currentName && (nextNote ?? "") === (currentNote ?? "")) return null;
  return nextNote ? { name: nextName, note: nextNote } : { name: nextName };
}

export const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
export const modKey = isMac ? "⌘" : "Ctrl";

export function formatBytes(bytes?: number): string {
  if (!bytes || bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
